import React from 'react';
import { AuditLogs, Dynamo } from '../../../primary-record-types';
import Stack from '../../v2BaseComponents/Stack';
import Text from '../../v2BaseComponents/Text';
import { getSpacing } from '../../../utils/spacing';
import { getIntl } from '../../../utils/intl';
import { useNestedNavigation } from '../../../utils/appNavigationNames';
import CollaboratorCard from '../../shared/CollaboratorCard';
import InvitationCard from '../../shared/InvitationCard';
import { UnloadedCareTeamMemberCard } from '../../shared/CareTeamMemberCard';
import { resourceCategoryMessages } from '../../../utils/sharedMessages';
import { UnloadedEndpointCard } from '../../shared/EndpointCard/EndpointCard';
import { UnloadedNoteCard } from '../../shared/NoteCard';
import { UnloadedResourceCard } from '../../shared/ResourceCard';
import ProfileCard from '../../shared/ProfileCard';
import { UnloadedSharedReportCard } from '../../shared/SharedReportCard';
import { UnloadedFileCard } from '../FileCard';
import { useIsMobileLayout } from '../../../hooks/useIsMobileLayout';
import { UnloadedOrgInvoiceIdInvoiceCard } from '../InvoiceCard';
import { UnloadedProfileDirectiveCard } from '../ProfileDirectiveCard';

const intl = getIntl('screens.mainApp.activity.referenceCards', {
    timeStamp: 'Time: ',
    medicalProfile: 'Medical Profile',
    collaborator: 'Collaborator',
    note: 'Note',
    endpoint: 'Connected Health System',
    deletedEndpoint: 'Deleted Health System',
    invitation: 'Invitation',
    careTeamMember: 'Care Team Member',
    partiallyInvalidHeader: 'Activity Is Missing Some Details',
    partiallyInvalidBody:
        'This activity is missing some details.  Some of the details may have been deleted or are no longer available and are not viewable.',
    deleteAuditLog: 'Delete Activity Details',
    deleteNotificationHeader: 'Are you sure you want to delete these activity details? This cannot be undone.',
    deleteButton: 'Yes, delete',
    cancelButton: 'No, cancel',
    sharedReport: 'Shared Report',
    file: 'File',
    unauthorized: 'Unauthorized',
    unauthorizedContent:
        'You are not authorized to view this content.  You may have lost access to details of this activity since it has been created.',
    invoice: 'Invoice',
    codeStatus: 'Code Status',
    legalDecisionMaker: 'Legal Decision Maker',
});

export type ReferenceCardsOwnProps = {
    auditLog: Dynamo.AuditLog;
    profileId: string;
};

export type ReferenceCardsProps = ReferenceCardsOwnProps;

const hasReference = <TReference extends AuditLogs.Reference | AuditLogs.AuditLogValue<any, any>>(
    auditLog: any,
    key: string,
): auditLog is AuditLogs.AuditLogWithReference<TReference> => {
    if (key === 'message') {
        return auditLog.references?.[key] !== undefined;
    } else {
        if (Array.isArray(auditLog.references?.[key])) {
            return auditLog.references?.[key]?.length > 0;
        } else {
            return auditLog.references?.[key] !== undefined;
        }
    }
};

function renderMaybeReferenceList<TReference>(
    reference: AuditLogs.SingleOrListAuditLogReferenceValue<TReference>,
    render: (reference: AuditLogs.SingleAuditLogReferenceValue<TReference>) => React.ReactElement,
): React.ReactElement {
    const asArray = Array.isArray(reference) ? reference : [reference];

    return (
        <>
            {asArray
                .filter((_) => !!_?.references)
                .map((ref) => {
                    return <React.Fragment key={ref.id}>{render(ref)}</React.Fragment>;
                })}
        </>
    );
}

function getSingleReference<TReference>(
    reference: AuditLogs.SingleOrListAuditLogReferenceValue<TReference>,
): AuditLogs.SingleAuditLogReferenceValue<TReference> {
    if (Array.isArray(reference)) {
        console.error('Expected single reference but got an array', reference);
        return reference[0];
    } else {
        return reference;
    }
}

const ReferenceCards: React.FC<ReferenceCardsProps> = ({ auditLog, profileId }) => {
    const isMobile = useIsMobileLayout();
    const { nestedProfileNavigation } = useNestedNavigation();

    const renderLabeledCard = (card: React.ReactElement, label: string): React.ReactElement => {
        return (
            <Stack vertical fullWidth space={getSpacing(0.25)}>
                {card}
                <Stack horizontal fullWidth style={{ paddingLeft: getSpacing(2) }}>
                    <Text variant="caption-1">{label}</Text>
                </Stack>
            </Stack>
        );
    };

    return (
        <>
            {hasReference<AuditLogs.ProfileReference>(auditLog, 'profile')
                ? renderMaybeReferenceList(auditLog.references.profile, (profile) =>
                      renderLabeledCard(
                          <ProfileCard
                              profileId={getSingleReference(profile).id}
                              skeletonFallback
                              onPress={() => {
                                  if (isMobile) {
                                      nestedProfileNavigation('profile', {
                                          firstName: profile.references.firstName || '',
                                          profileId: getSingleReference(profile).id,
                                      });
                                  } else {
                                      nestedProfileNavigation('profile-activity', {
                                          profileId: getSingleReference(profile).id,
                                      });
                                  }
                              }}
                          />,
                          intl('medicalProfile'),
                      ),
                  )
                : null}
            {hasReference<AuditLogs.CollaboratorReference>(auditLog, 'collaborator')
                ? renderMaybeReferenceList(auditLog.references.collaborator, (collaborator) =>
                      renderLabeledCard(
                          <CollaboratorCard
                              medicalProfileId={profileId}
                              collaboratorId={getSingleReference(collaborator).id}
                              skeletonFallback
                              onPress={() => {
                                  nestedProfileNavigation('collaborator', {
                                      collaboratorId: getSingleReference(collaborator).id,
                                      profileId: getSingleReference(auditLog.references.profile).id,
                                  });
                              }}
                          />,
                          intl('collaborator'),
                      ),
                  )
                : null}
            {hasReference<AuditLogs.CollaboratorInviteeReference>(auditLog, 'collaboratorInvitee')
                ? renderMaybeReferenceList(auditLog.references.collaboratorInvitee, (collaboratorInvitee) =>
                      renderLabeledCard(
                          <InvitationCard
                              medicalProfileId={profileId}
                              invitationId={getSingleReference(collaboratorInvitee).id}
                              skeletonFallback
                              onPress={() => {
                                  nestedProfileNavigation('invite-details', {
                                      invitationId: getSingleReference(collaboratorInvitee).id,
                                      profileId: getSingleReference(auditLog.references.profile).id,
                                  });
                              }}
                          />,
                          intl('invitation'),
                      ),
                  )
                : null}
            {hasReference<AuditLogs.CareTeamMemberReference>(auditLog, 'careTeamMember')
                ? renderMaybeReferenceList(auditLog.references.careTeamMember, (careTeamMember) =>
                      renderLabeledCard(
                          <UnloadedCareTeamMemberCard
                              medicalProfileId={profileId}
                              careTeamMemberId={getSingleReference(careTeamMember).id}
                              skeletonFallback
                              onPress={() => {
                                  nestedProfileNavigation('care-team-member', {
                                      careTeamMemberId: getSingleReference(careTeamMember).id,
                                      profileId: getSingleReference(auditLog.references.profile).id,
                                  });
                              }}
                          />,
                          intl('careTeamMember'),
                      ),
                  )
                : null}
            {hasReference<AuditLogs.ResourceReference>(auditLog, 'resource')
                ? renderMaybeReferenceList(auditLog.references.resource, (resource) =>
                      renderLabeledCard(
                          <UnloadedResourceCard
                              profileId={profileId}
                              resourceId={getSingleReference(resource).id}
                              category={resource.references.category}
                              skeletonFallback
                              onPress={() => {
                                  nestedProfileNavigation('data-details', {
                                      resourceId: getSingleReference(resource).id,
                                      profileId: getSingleReference(auditLog.references.profile).id,
                                      category: resource.references.category,
                                  });
                              }}
                          />,
                          resourceCategoryMessages(
                              getSingleReference(auditLog.references.resource).references.category,
                          ),
                      ),
                  )
                : null}
            {hasReference<AuditLogs.NoteReference>(auditLog, 'note')
                ? renderMaybeReferenceList(auditLog.references.note, (note) =>
                      renderLabeledCard(
                          <UnloadedNoteCard
                              noteId={getSingleReference(note).id}
                              skeletonFallback
                              onPress={(note) => {
                                  if (note.medicalProfileId) {
                                      nestedProfileNavigation('note', {
                                          noteId: note.noteId,
                                          noteEntityType: note.entityType,
                                          profileId: note.medicalProfileId,
                                      });
                                  }
                              }}
                          />,
                          intl('note'),
                      ),
                  )
                : null}
            {hasReference<AuditLogs.OneUpHealthConnectedHealthSystemReference>(
                auditLog,
                'oneUpHealthConnectedHealthSystem',
            )
                ? renderMaybeReferenceList(
                      auditLog.references.oneUpHealthConnectedHealthSystem,
                      (oneUpHealthConnectedHealthSystem) =>
                          renderLabeledCard(
                              <UnloadedEndpointCard
                                  endpointId={getSingleReference(oneUpHealthConnectedHealthSystem).id}
                                  isDisabled={false}
                                  skeletonFallback
                                  hideAutomaticRefreshMessage
                              />,
                              intl('endpoint'),
                          ),
                  )
                : null}
            {hasReference<AuditLogs.OneUpHealthHealthSystemReference>(auditLog, 'oneUpHealthSystem')
                ? renderMaybeReferenceList(auditLog.references.oneUpHealthSystem, (oneUpHealthSystem) =>
                      renderLabeledCard(
                          <UnloadedEndpointCard
                              endpointId={getSingleReference(oneUpHealthSystem).id}
                              isDisabled={false}
                              skeletonFallback
                              hideAutomaticRefreshMessage
                          />,
                          intl('deletedEndpoint'),
                      ),
                  )
                : null}
            {hasReference<AuditLogs.SharedReportReference>(auditLog, 'sharedReport')
                ? renderMaybeReferenceList(auditLog.references.sharedReport, (sharedReport) =>
                      renderLabeledCard(
                          <UnloadedSharedReportCard
                              onPress={() => {
                                  nestedProfileNavigation('shared-report', {
                                      profileId: getSingleReference(auditLog.references.profile).id,
                                      sharedReportId: getSingleReference(sharedReport).id,
                                  });
                              }}
                              sharedReportId={getSingleReference(sharedReport).id}
                              medicalProfileId={getSingleReference(auditLog.references.profile).id}
                          />,
                          intl('sharedReport'),
                      ),
                  )
                : null}
            {hasReference<AuditLogs.MedicalProfileFileReference>(auditLog, 'medicalProfileFile')
                ? renderMaybeReferenceList(auditLog.references.medicalProfileFile, (medicalProfileFile) =>
                      renderLabeledCard(
                          <UnloadedFileCard
                              onPress={() => {
                                  nestedProfileNavigation('file', {
                                      profileId: getSingleReference(auditLog.references.profile).id,
                                      fileId: getSingleReference(medicalProfileFile).id,
                                  });
                              }}
                              medicalProfileFileId={getSingleReference(medicalProfileFile).id}
                              medicalProfileId={getSingleReference(auditLog.references.profile).id}
                              skeletonFallback
                          />,
                          intl('file'),
                      ),
                  )
                : null}
            {hasReference<AuditLogs.OrganizationInvoiceReference>(auditLog, 'organizationInvoice')
                ? renderMaybeReferenceList(auditLog.references.organizationInvoice, (invoice) =>
                      renderLabeledCard(
                          <UnloadedOrgInvoiceIdInvoiceCard
                              onPress={(collaboratorId) => {
                                  nestedProfileNavigation('collaborator-invoice', {
                                      collaboratorId,
                                      invoiceId: getSingleReference(invoice).id,
                                      profileId: getSingleReference(auditLog.references.profile).id,
                                  });
                              }}
                              invoiceId={getSingleReference(invoice).id}
                              organizationId={getSingleReference(auditLog.references.organization).id}
                              medicalProfileId={getSingleReference(auditLog.references.profile).id}
                          />,
                          intl('invoice'),
                      ),
                  )
                : null}
            {hasReference<AuditLogs.DirectiveReference>(auditLog, 'directive')
                ? renderMaybeReferenceList(auditLog.references.directive, () =>
                      renderLabeledCard(
                          <UnloadedProfileDirectiveCard
                              directiveType={
                                  auditLog.type === 'code-status-directive-creation' ||
                                  auditLog.type === 'code-status-directive-update'
                                      ? 'code-status'
                                      : 'poa'
                              }
                              medicalProfileId={getSingleReference(auditLog.references.profile).id}
                              onPress={() => {
                                  nestedProfileNavigation('edit', {
                                      profileId: getSingleReference(auditLog.references.profile).id,
                                      firstName: '',
                                      defaultTab: 'advancedDirectives',
                                  });
                              }}
                          />,
                          intl(
                              auditLog.type === 'code-status-directive-creation' ||
                                  auditLog.type === 'code-status-directive-update'
                                  ? 'codeStatus'
                                  : 'legalDecisionMaker',
                          ),
                      ),
                  )
                : null}
        </>
    );
};

export default React.memo(ReferenceCards);
